import type { FlightPlan, FlightPlanStatus, FlightPurpose, CrewMember, CrewRole } from '@/shared/types'
import { AIRCRAFT } from './aircraft'
import { PILOTS } from './pilots'
import { FLIGHT_ROUTES } from './routes'
import { AIRSPACES } from './airspaces'

export const CREW_ROLE_LABEL: Record<CrewRole, string> = {
  captain: 'Cơ trưởng',
  'co-pilot': 'Cơ phó',
  instructor: 'Giáo viên bay',
  trainee: 'Học viên',
  observer: 'Quan sát viên',
  examiner: 'Đánh giá viên',
  navigator: 'Hoa tiêu',
  gunner: 'Xạ thủ',
}

export const CREW_ROLE_SHORT: Record<CrewRole, string> = {
  captain: 'CT',
  'co-pilot': 'CP',
  instructor: 'GV',
  trainee: 'HV',
  observer: 'QS',
  examiner: 'ĐG',
  navigator: 'HT',
  gunner: 'XT',
}

export const CREW_ROLE_COLOR: Record<CrewRole, string> = {
  captain: 'red',
  'co-pilot': 'blue',
  instructor: 'purple',
  trainee: 'cyan',
  observer: 'default',
  examiner: 'magenta',
  navigator: 'gold',
  gunner: 'orange',
}

const ROLES_BY_PURPOSE: Record<FlightPurpose, CrewRole[]> = {
  training: ['instructor', 'trainee'],
  'combat-readiness': ['captain', 'co-pilot'],
  transport: ['captain', 'co-pilot', 'navigator'],
  reconnaissance: ['captain', 'observer'],
  patrol: ['captain', 'co-pilot'],
  sar: ['captain', 'co-pilot', 'observer'],
  test: ['examiner', 'captain'],
}

const PURPOSES: FlightPurpose[] = ['training', 'training', 'combat-readiness', 'transport', 'reconnaissance', 'patrol', 'sar', 'test']

const TODAY = new Date('2026-05-11T00:00:00')

function computeStatus(takeoffISO: string, landingISO: string, i: number): FlightPlanStatus {
  const takeoff = new Date(takeoffISO)
  const landing = new Date(landingISO)
  if (landing < TODAY) {
    return i % 13 === 0 ? 'cancelled' : 'completed'
  }
  if (takeoff <= TODAY && TODAY <= landing) {
    return 'in-progress'
  }
  return i % 23 === 0 ? 'cancelled' : 'scheduled'
}

const PURPOSE_LABEL: Record<FlightPurpose, string> = {
  training: 'Huấn luyện bay',
  'combat-readiness': 'Trực sẵn sàng chiến đấu',
  transport: 'Vận chuyển',
  reconnaissance: 'Trinh sát',
  sar: 'Tìm kiếm cứu nạn',
  patrol: 'Tuần tra',
  test: 'Bay thử kỹ thuật',
}

function pad(n: number, w: number): string {
  return String(n).padStart(w, '0')
}

function buildCrew(
  i: number,
  sortiePurposes: FlightPurpose[],
  aircraftIds: string[],
): CrewMember[] {
  const crew: CrewMember[] = []
  let pilotIdx = 0
  aircraftIds.forEach((acId, idx) => {
    const roles = ROLES_BY_PURPOSE[sortiePurposes[idx]]
    roles.forEach((role, roleIdx) => {
      crew.push({
        pilotId: PILOTS[(i * 7 + pilotIdx++) % PILOTS.length].id,
        role,
        primary: roleIdx === 0,
        aircraftId: acId,
      })
    })
  })
  return crew
}

function buildSortiePurposes(i: number, planPurpose: FlightPurpose, count: number): FlightPurpose[] {
  const out: FlightPurpose[] = []
  for (let k = 0; k < count; k++) {
    // 60% chuyến giữ mục đích chính, 40% mix sang mục đích khác
    const r = (i * 13 + k * 7) % 10
    if (r < 6 || k === 0) {
      out.push(planPurpose)
    } else {
      out.push(PURPOSES[(i + k * 3 + 1) % PURPOSES.length])
    }
  }
  return out
}

// Hard-code distribution of 60 sorties to 3 active annual plans (921, 923, 918)
const ANNUAL_PLAN_IDS = ['ap-2026-921', 'ap-2026-923', 'ap-2026-918']

function generatePlans(): FlightPlan[] {
  const list: FlightPlan[] = []
  for (let i = 0; i < 60; i++) {
    const route = FLIGHT_ROUTES[i % FLIGHT_ROUTES.length]
    const planPurpose = PURPOSES[i % PURPOSES.length]
    const aircraftCount = planPurpose === 'transport' ? 1 : planPurpose === 'training' ? 2 : 4
    const aircraftIds = Array.from({ length: aircraftCount }, (_, k) => AIRCRAFT[(i * 3 + k) % AIRCRAFT.length].id)
    const sortiePurposes = buildSortiePurposes(i, planPurpose, aircraftCount)
    const crew = buildCrew(i, sortiePurposes, aircraftIds)
    const airspaceIds = [AIRSPACES[i % AIRSPACES.length].id]
    if (i % 3 === 0) airspaceIds.push(AIRSPACES[(i + 4) % AIRSPACES.length].id)

    const day = (i % 28) + 1
    const hour = 6 + (i % 12)
    const duration = route.estimatedDurationMin
    const annualPlanId = ANNUAL_PLAN_IDS[i % ANNUAL_PLAN_IDS.length]
    const month = (i % 12) + 1
    const scheduleId = `fs-${annualPlanId}-${pad(month, 2)}`
    const scheduledTakeoff = `2026-${pad(month, 2)}-${pad(day, 2)}T${pad(hour, 2)}:00:00`
    const scheduledLanding = `2026-${pad(month, 2)}-${pad(day, 2)}T${pad(hour + Math.ceil(duration / 60), 2)}:${pad(duration % 60, 2)}:00`
    const status = computeStatus(scheduledTakeoff, scheduledLanding, i)

    const unique = Array.from(new Set(sortiePurposes))
    const titleLabel = unique.length === 1 ? PURPOSE_LABEL[unique[0]] : 'Hỗn hợp mục đích'
    const descLabel =
      unique.length === 1
        ? PURPOSE_LABEL[unique[0]].toLowerCase()
        : `${unique.length} mục đích bay`

    list.push({
      id: `fp-${i + 1}`,
      code: `LB-2026-${pad(i + 1, 5)}`,
      sortiePurposes,
      title: `${titleLabel} — ${route.code}`,
      description: `Thực hiện ${descLabel} trên đường bay ${route.name}.`,
      scheduledTakeoff,
      scheduledLanding,
      durationMin: duration,
      unitId: PILOTS[(i * 5) % PILOTS.length].unitId,
      crew,
      aircraftIds,
      airspaceIds,
      routeId: route.id,
      fromAirportId: route.fromAirportId,
      toAirportId: route.toAirportId,
      status,
      createdBy: 'Đại uý Nguyễn Hữu Nam',
      createdAt: `2025-12-${pad((i % 28) + 1, 2)}T${pad(8 + (i % 8), 2)}:15:00`,
      scheduleId,
      annualPlanId,
      notes: i % 4 === 0 ? 'Bay đôi, hiệp đồng radar trận địa N3.' : undefined,
    })
  }
  return list
}

/** Trả về mục đích của 1 chuyến (sortie) cụ thể trong kế hoạch bay. Fallback chuyến đầu nếu idx ngoài range. */
export function getSortiePurpose(plan: FlightPlan, sortieIdx: number): FlightPurpose {
  return plan.sortiePurposes[sortieIdx] ?? plan.sortiePurposes[0]
}

/** Mục đích phổ biến nhất trong tất cả chuyến bay của kế hoạch — dùng cho hiển thị plan-level. */
export function getPlanPrimaryPurpose(plan: FlightPlan): FlightPurpose {
  const counts: Partial<Record<FlightPurpose, number>> = {}
  let max = 0
  let primary: FlightPurpose = plan.sortiePurposes[0]
  for (const p of plan.sortiePurposes) {
    const c = (counts[p] ?? 0) + 1
    counts[p] = c
    if (c > max) {
      max = c
      primary = p
    }
  }
  return primary
}

/** Danh sách mục đích duy nhất xuất hiện trong các chuyến bay. */
export function getPlanUniquePurposes(plan: FlightPlan): FlightPurpose[] {
  return Array.from(new Set(plan.sortiePurposes))
}

export const FLIGHT_PLANS: FlightPlan[] = generatePlans()
export const FLIGHT_PLANS_BY_ID: Record<string, FlightPlan> = Object.fromEntries(
  FLIGHT_PLANS.map((f) => [f.id, f]),
)

export const FLIGHT_PLAN_STATUS_LABEL: Record<FlightPlanStatus, string> = {
  scheduled: 'Chưa thực hiện',
  'in-progress': 'Đang thực hiện',
  completed: 'Đã hoàn thành',
  cancelled: 'Đã huỷ',
}

export const FLIGHT_PLAN_STATUS_COLOR: Record<FlightPlanStatus, string> = {
  scheduled: 'blue',
  'in-progress': 'cyan',
  completed: 'success',
  cancelled: 'default',
}

export const PURPOSE_LABEL_MAP = PURPOSE_LABEL
